import React, { useState, useEffect } from "react";
import { User, NudgeMessage, Organization } from "@pollyslack/hub/src/types";
import moment from "moment";
import { ExpanderToggle } from "src/components/Expander";
import { Code } from "src/components/Code";
import * as UserAPI from "src/services/api/User";
import * as SlackMessagesAPI from "src/services/api/SlackMessages";
import styled from "styled-components";
import Loader from "src/common/Loader";
import { UserLink } from "src/components/DocumentLink";

const StyledTable = styled.table.attrs({
  className: "table table-dark table-sm table-hover overflow-auto",
})``;

const TableCell = styled.td`
  vertical-align: middle !important;
`;

const CaretCell = styled(TableCell)`
  width: 40px;
`;

const JsonContent = styled.div`
  max-height: 400px;
  overflow-y: auto;
  width: 100%;
`;

const ExpandedContent = styled.td.attrs<{ colSpan: number }>((props) => ({
  colSpan: props.colSpan,
  className: "p-3",
}))`
  max-width: 0;
`;

interface LifecycleMessageRowData {
  user: User;
  message: NudgeMessage;
}

const NudgeMessageRow: React.FC<{
  data: LifecycleMessageRowData;
  showUser: boolean;
}> = ({ data, showUser }) => {
  const { user, message } = data;
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [slackMessage, setSlackMessage] = useState(null);

  const toggle = async () => {
    if (!isOpen && !slackMessage && message.messageId) {
      setIsLoading(true);
      try {
        const slackMessage = await SlackMessagesAPI.info(message.messageId);
        setSlackMessage(slackMessage);
      } catch (error) {
        console.error("Failed to fetch Slack message", error);
      } finally {
        setIsLoading(false);
      }
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <tr>
        <CaretCell>
          {message.messageId && (
            <ExpanderToggle expanded={isOpen} toggle={toggle} label="" />
          )}
        </CaretCell>
        <TableCell>{message.messageId || "—"}</TableCell>
        {showUser && (
          <TableCell>
            {user.profile.user_id ? (
              <UserLink docId={user._id}>{user.profile.user_id}</UserLink>
            ) : (
              "—"
            )}
          </TableCell>
        )}
        <TableCell>{user.profile.email || "—"}</TableCell>
        <TableCell>{user.profile.title || "—"}</TableCell>
        <TableCell>{message.messageName || "—"}</TableCell>
        <TableCell>
          {message.sendDate
            ? moment(message.sendDate).format("MM/DD/YYYY")
            : "—"}
        </TableCell>
        <TableCell>{message.isValid ? "Yes" : "No"}</TableCell>
        <TableCell>{message.isDeleted ? "Yes" : "No"}</TableCell>
      </tr>
      {isOpen && message.messageId && (
        <tr>
          <ExpandedContent colSpan={showUser ? 9 : 8}>
            {isLoading ? (
              <Loader />
            ) : (
              <JsonContent>
                <Code object={slackMessage} />
              </JsonContent>
            )}
          </ExpandedContent>
        </tr>
      )}
    </>
  );
};

export const LifecycleMessagesTable: React.FC<{
  user?: User;
  organization?: Organization;
}> = ({ user, organization }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (organization) {
      setIsLoading(true);
      UserAPI.list({ search: organization._id })
        .then((users) => {
          setUsers(users);
        })
        .catch((e) => {
          alert(e);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (user) {
      setUsers([user]);
    }
  }, [user, organization]);

  const rows: LifecycleMessageRowData[] = users
    .flatMap((u) =>
      (u.profile?.nudgeMessages || []).map((message) => ({
        user: u,
        message,
      })),
    )
    .sort(
      (a, b) =>
        new Date(b.message.sendDate).getTime() -
        new Date(a.message.sendDate).getTime(),
    );

  if (isLoading) return <Loader />;

  return (
    <StyledTable>
      <thead>
        <tr>
          <th />
          <th>Message ID</th>
          {organization && <th>User ID</th>}
          <th>User Email</th>
          <th>User Title</th>
          <th>Message Name</th>
          <th>Send Date</th>
          <th>Is Valid</th>
          <th>Is Deleted</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <NudgeMessageRow
            key={`${row.user._id}-${i}`}
            data={row}
            showUser={!!organization}
          />
        ))}
      </tbody>
    </StyledTable>
  );
};
